/**
 * Which stream is fullscreen — so every other camera can stop.
 *
 * A tile goes fullscreen through the browser's Fullscreen API (`VideoTile` →
 * `handleMaximize` → `element.requestFullscreen()`), and the rest of the grid
 * stays mounted underneath it. rendered-tiles therefore still reports every
 * tile on the page as on screen, and lastN kept all sixteen streaming at their
 * full rate behind a picture that covers them.
 *
 * Elements that can go fullscreen carry `data-fullscreen-key`: a tile's own
 * stream key (`peer:source:video`, the same triple lastN uses), or the empty
 * string for an element that shows no remote camera — your own tile, the
 * presentation. The browser's fullscreen element is the source of truth, so
 * Esc, the browser's own exit and the tile's button all land in the same place
 * without the app having to remember which way it went.
 *
 * App-wide fullscreen (the More menu) puts `<html>` fullscreen. It carries no
 * key, so it reads as null and lastN behaves exactly as before.
 */

export const FULLSCREEN_KEY_ATTR = 'data-fullscreen-key'

type FullscreenDocument = Pick<Document, 'fullscreenElement'> & {
  webkitFullscreenElement?: Element | null
}

/** The fullscreen element's key; null when nothing keyed is fullscreen. */
export function fullscreenKey(
  doc: FullscreenDocument | undefined = typeof document === 'undefined' ? undefined : document,
): string | null {
  // Safari still reports only the prefixed property on some versions.
  const el = doc?.fullscreenElement ?? doc?.webkitFullscreenElement ?? null
  if (!el) return null
  return el.getAttribute(FULLSCREEN_KEY_ATTR)
}

export function subscribe(onChange: () => void): () => void {
  document.addEventListener('fullscreenchange', onChange)
  document.addEventListener('webkitfullscreenchange', onChange)
  return () => {
    document.removeEventListener('fullscreenchange', onChange)
    document.removeEventListener('webkitfullscreenchange', onChange)
  }
}

/** A string or null — stable by value, as useSyncExternalStore requires. */
export const getSnapshot = (): string | null => fullscreenKey()
export const getServerSnapshot = (): string | null => null

/**
 * The cameras lastN should keep flowing.
 *
 * Fullscreen wins over everything, pinned peers and the active speaker
 * included: nothing else can be seen, and their audio is not touched. An empty
 * key (your own tile, the presentation) keeps no remote camera at all. Screen
 * shares are outside this set's scope entirely — lastN never caps them — so a
 * presentation keeps flowing behind a fullscreen camera.
 *
 * Not fullscreen: what is painted, plus pinned peers and the active speaker,
 * who are never paused. null when nothing is painted yet (or rendered-tiles
 * found drift), which disengages the cap.
 */
export function visibleCameraKeys(input: {
  fullscreenKey: string | null
  renderedKeys: ReadonlySet<string> | null
  pinnedKeys: Iterable<string>
  activeSpeakerKey: string | null
}): Set<string> | null {
  if (input.fullscreenKey !== null) {
    return new Set(input.fullscreenKey ? [input.fullscreenKey] : [])
  }
  if (!input.renderedKeys) return null
  const keys = new Set(input.renderedKeys)
  for (const key of input.pinnedKeys) keys.add(key)
  if (input.activeSpeakerKey) keys.add(input.activeSpeakerKey)
  return keys
}
